import { useEffect, useCallback } from 'react'
import { supabase } from '../services/supabase'
import { useAuthStore } from '../store/authStore'
import { api } from '../services/api'
import { initializeSocket, disconnectSocket, updateSocketToken } from '../services/socket'
import type { Player } from '@territory-nexus/shared'

export const useAuth = () => {
  const { player, hasSession, hasProfile, isLoading, setAuth } = useAuthStore()

  const fetchProfile = useCallback(async (retries = 2): Promise<{ player: Player | null; serverError: boolean }> => {
    for (let attempt = 0; attempt <= retries; attempt++) {
      try {
        const response = await api.get<{ success: boolean; data: Player }>('/auth/profile')
        if (response.data.success && response.data.data) {
          return { player: response.data.data, serverError: false }
        }
        return { player: null, serverError: false } // 200 but no profile = genuinely new user
      } catch (err: unknown) {
        const status = (err as { response?: { status?: number } })?.response?.status
        // Auth failure, no profile, or rate-limited — don't retry
        if (status === 401 || status === 403 || status === 404 || status === 429) {
          return { player: null, serverError: false }
        }
        // Server error or network issue — retry with backoff
        if (attempt < retries) {
          await new Promise(r => setTimeout(r, 1500 * (attempt + 1)))
          continue
        }
        // All retries exhausted — server is down
        return { player: null, serverError: true }
      }
    }
    return { player: null, serverError: true }
  }, [])

  useEffect(() => {
    let mounted = true
    let bootstrapped = false

    const handleSession = async (session: { access_token: string } | null) => {
      if (!mounted) return

      if (session) {
        const { player: playerData, serverError } = await fetchProfile()
        if (!mounted) return

        if (serverError) {
          // Server unreachable after retries — sign out cleanly so user
          // gets the login screen instead of being stuck or sent to register
          disconnectSocket()
          try { await supabase.auth.signOut() } catch { /* ignore */ }
          setAuth(null, null)
          return
        }

        setAuth(session.access_token, playerData)
        if (playerData) {
          try { initializeSocket(session.access_token) } catch { /* ignore */ }
        }
      } else {
        disconnectSocket()
        if (mounted) setAuth(null, null)
      }
    }

    // Bootstrap: check for existing session (e.g. page refresh)
    const bootstrap = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession()
        if (mounted && !bootstrapped) {
          bootstrapped = true
          await handleSession(session ?? null)
        }
      } catch {
        // Failed — clear and show login
        if (mounted && !bootstrapped) {
          bootstrapped = true
          setAuth(null, null)
        }
      }
    }
    bootstrap()

    // Safety net: if bootstrap takes too long (server down, network issues),
    // stop loading so the user isn't stuck on a spinner forever.
    const safetyTimer = setTimeout(() => {
      if (mounted && !bootstrapped) {
        bootstrapped = true
        setAuth(null, null)
      }
    }, 15000)

    // Listen for ongoing auth changes (sign in, sign out, token refresh)
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (!mounted) return

      if (event === 'TOKEN_REFRESHED' && session) {
        // Update the cached token in the store and socket
        const store = useAuthStore.getState()
        store.setAuth(session.access_token, store.player)
        updateSocketToken(session.access_token)
        return
      }

      // For SIGNED_IN after bootstrap, re-handle session (e.g. user just logged in)
      // For SIGNED_OUT, clear everything
      if (bootstrapped) {
        await handleSession(session ?? null)
      }
    })

    return () => {
      mounted = false
      clearTimeout(safetyTimer)
      subscription.unsubscribe()
    }
  }, [setAuth, fetchProfile])

  return { player, hasSession, hasProfile, isLoading }
}
